import '../../modules/mode-components';

import * as angular from 'angular';
import { assign, find } from 'lodash';
import { combineLatest as observableCombineLatest, from as observableFrom, Observable } from 'rxjs';
import { filter, first, map, tap } from 'rxjs/operators';

import { AppStore } from '~/types/main.types';

import { ReportViewerFacade } from '@mode/report-viewer/domain';
import { ReportTypes } from '@mode/shared/contract-common';
import { dispatchEvent } from '@mode/shared/util-js';
import { CHART_OPTION_TYPES_WITH_KEY } from '../../common/constants';
import { accountSelector } from '../../reducers/selectors';
import { NotebookVizState } from '@mode/legacy/types-app-state';
import { ReportDataset } from '@mode/legacy/types-app-state';
import { NotebookSelectors } from '../../selectors/notebook.selectors';
import { SelectedReportSelectors } from '../../selectors/selected-report.selectors';
import { FeatureToggleService } from '../../services/feature-toggle-service';
import { ReportStateService } from '../../services/report-state-service';

export interface VizLightboxObject {
  id: string;
  view: any;
}

class VizContainerController {
  static $inject = [
    '$scope',
    '$timeout',
    'ReportStateService',
    'ReportViewerFacade',
    '$window',
    'RxStoreService',
    'FeatureToggleService',
    'FeatureFlagsFacade',
  ];

  public visualization: VizLightboxObject;
  public dataset: any;
  public view: any;
  public owner: any;
  public vizType: string;
  public vizTitle: any;
  public vizTitleDisplay: string;
  public onDismiss: any;
  public errorMessage: string;
  public sidebarOpen: boolean;

  private account$: Observable<any>;
  private exploreEnabled$: Observable<boolean>;
  private chartDataExportEnabled$: Observable<boolean>;
  private hasRunBanner$: Observable<any>;

  constructor(
    public $scope,
    private $timeout,
    private reportStateService: ReportStateService,
    private reportViewerFacade: ReportViewerFacade,
    private $window,
    private store: AppStore,
    private featureToggleService: FeatureToggleService
  ) {}

  $onInit() {
    this.setupVizOptions();
    this.$scope.$watch('ctrl.sidebarOpen', (val) => {
      // trigger resize whenever sidebar is toggled
      dispatchEvent(this.$window, 'resize');
    });
    this.account$ = this.store.select(accountSelector);
    this.exploreEnabled$ = this.featureToggleService.featureExploreEnabled$;
    this.chartDataExportEnabled$ = this.featureToggleService.featureChartDataExportEnabled$;
    this.hasRunBanner$ = this.reportViewerFacade.reportRunBannerType$.pipe(
      map((type) => type !== ReportTypes.ReportRunBannerTypes.NoBanner)
    );
  }

  setupVizOptions() {
    // viz ids are unique, so subscribe to the first viz of this id to be found
    // among all query and notebook visualizations
    observableCombineLatest(
      observableFrom(this.reportStateService.getQueryVisualizations()),
      this.store.select(NotebookSelectors.createVizRenderSelector(this.visualization.id))
    )
      .pipe(
        map(([queries, notebookViz]) => [notebookViz || this.getQueryViz(this.visualization.id, queries), queries]),
        first(([vizModel, queries]) => vizModel)
      )
      .subscribe(
        ([vizModel, queries]) => {
          if (vizModel) {
            this.getVizModelDataset(vizModel, queries);
          } else {
            this.handleError();
          }
        },
        (err) => console.error(err)
      );
  }

  getVizModelDataset(vizModel, queries: any[]) {
    if (vizModel.view_vegas && vizModel.view_version === 2) {
      throw new Error('Vegas chart rendered with <visualization-container/>, should be <chart-lightbox-container/>');
    } else if (vizModel.view && vizModel.view_version === 1) {
      this.vizTitle = vizModel.view.chartTitle || CHART_OPTION_TYPES_WITH_KEY[vizModel.view.selectedChart].name;
    } else {
      this.vizTitle = '';
    }

    // python viz doesn't need dataset, so don't bother with that
    if (vizModel._name !== 'pythonVisualization') {
      this.store
        .select(SelectedReportSelectors.createFilteredDatasetSelector(vizModel.parent))
        .pipe(
          filter((ds) => ds != null),
          first()
        )
        .subscribe((dataset) => {
          this.renderViz(vizModel, queries, dataset);
        });
    } else {
      this.renderNotebookViz(vizModel);
    }
  }

  renderNotebookViz(vizModel) {
    this.setVizType('python');
    this.vizTitle = vizModel.title;

    if (vizModel.state === 'loading') {
      // make sure we wait for python to finish
      this.store
        .select(NotebookSelectors.createVizRenderSelector(this.visualization.id))
        .pipe(first((viz) => viz.state !== NotebookVizState.Loading))
        .subscribe((viz) => {
          this.view = viz.results;
        });
    } else {
      this.view = vizModel.results;
    }

    this.vizTitleDisplay = this.vizTitle && this.vizTitle.length ? `Viewing ${this.vizTitle}` : `Viewing`;
  }

  renderViz(vizModel, queries: any[], dataset: ReportDataset) {
    // check that we have everything needed to render
    if (vizModel._name === 'queryTable' && vizModel.parent && dataset) {
      this.setVizType('table');
      this.vizTitle = find(queries, { token: vizModel.parent }).name;
      // load the table view options so the table component can read them
      this.compileViz.call(this, vizModel.view, dataset);
    } else if (vizModel._name === 'chart' && vizModel.view_version === 2) {
      throw new Error('Vegas chart rendered with <visualization-container/>, should be <chart-lightbox-container/>');
    } else if (vizModel._name === 'chart' && vizModel.parent && dataset) {
      this.setVizType(vizModel.view.selectedChart === 'pivotTable' ? 'pivot' : 'chart');
      this.compileViz.call(this, vizModel.view, dataset);
    } else {
      this.handleError();
    }

    if (this.vizType === 'pivot') {
      this.vizTitleDisplay = this.vizTitle && this.vizTitle.length ? `Exploring ${this.vizTitle}` : `Exploring`;
    } else {
      this.vizTitleDisplay = this.vizTitle && this.vizTitle.length ? `Viewing ${this.vizTitle}` : `Viewing`;
    }
  }

  getQueryViz(id: string, queries: any[]) {
    const queryVisualizations: any[] = queries.reduce(
      (acc, query) => acc.concat(query.storedCharts, query.storedTables),
      []
    );
    return find(queryVisualizations, { token: id });
  }

  setVizType(type: string) {
    this.vizType = type;
  }

  compileViz(view, dataset?) {
    this.dataset = dataset;
    this.view = view;
  }

  handleError() {
    this.errorMessage = 'Could not find visualization';
  }

  updatePivotTableView($view) {
    this.$timeout(() => {
      this.view = assign({}, this.view, { pivotTable: $view });
    });
  }

  openSidebar() {
    this.sidebarOpen = true;
  }

  toggleSidebar() {
    this.sidebarOpen = !this.sidebarOpen;
  }

  onDismissLightbox() {
    this.onDismiss();
  }
}

const VizContainer: angular.IComponentOptions = {
  bindings: {
    visualization: '<',
    owner: '<',
    sidebarOpen: '<',
    onDismiss: '&',
  },
  controller: VizContainerController,
  controllerAs: 'ctrl',
  template: `
<a class="sidebar-toggle" ng-if="ctrl.vizType === 'pivot'" ng-click="ctrl.toggleSidebar()" aria-hidden="false">
  <capra-icon icon="icon-collapse-panel" ng-class="ctrl.sidebarOpen ? 'collapse' : 'expand'"></capra-icon>
</a>

<!-- Pivot Table Sidebar -->
<div
  class="modal-sidebar chart-sidebar"
  ng-if="ctrl.vizType === 'pivot'"
  ng-show="ctrl.sidebarOpen">
  <pivot-table-settings
    token="ctrl.visualization.id"
    data="ctrl.dataset"
    view="ctrl.view.pivotTable"
    container-height=""
    on-settings-update="ctrl.updatePivotTableView($view)">
  </pivot-table-settings>
</div>
<div class="modal-container"
  ng-class="{
    'chart-content': ctrl.vizType === 'chart' || ctrl.vizType === 'pivot',
    'table-content': ctrl.vizType === 'table',
    'sidebar-enabled': ctrl.sidebarOpen
  }"
    pivot-resize>
  <!-- Chart and Pivot Table Component -->
  <mode-chart
    ng-if="ctrl.vizType === 'chart' || ctrl.vizType === 'pivot'"
    id="{{ ctrl.visualization.id }}"
    dataset="ctrl.dataset"
    set-view="ctrl.view"
    is-chart-visible="true"
    chart-exists=""
    viewer="">
  </mode-chart>
  <!-- Table Component -->
  <mode-table
    ng-if="ctrl.vizType === 'table'"
    id="table_{{ ctrl.visualization.id }}"
    dataset="ctrl.dataset"
    set-view="ctrl.view">
  </mode-table>
  <!-- Python Component -->
  <div class="viz-notebook-container" ng-if="ctrl.vizType === 'python'" ng-class="{'--has-run-banner': (ctrl.hasRunBanner$ | async:this)}" resize-cells>
    <mode-notebook-cell-result
      ng-repeat="result in ctrl.view"
      id="python_{{ ctrl.visualization.id }}"
      result="result">
    </mode-notebook-cell-result>
  </div>
  <div ng-if="ctrl.errorMessage"><h3>{{ ctrl.errorMessage }}</h3></div>
</div>
`,
};

angular.module('mode.components').component('visualizationContainer', VizContainer);
